<template>
  <div class="sales-invoices">
    <b-card>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          <feather-icon
            icon="FileTextIcon"
            size="20"
            class="mr-1"
          />
          Mes Factures de Commission
        </h4>
        <div class="d-flex gap-2">
          <b-form-select
            v-model="filters.payment_status"
            :options="statusOptions"
            size="sm"
            class="w-auto"
            @change="loadInvoices"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="loading"
            @click="loadInvoices"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              :class="{ 'spinning': loading }"
            />
            Actualiser
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <div
          v-if="loading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
          <p class="mt-2 text-muted">
            Chargement des factures...
          </p>
        </div>

        <div
          v-else-if="invoices.length === 0"
          class="text-center py-5"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted"
          />
          <p class="mt-2 text-muted">
            Aucune facture trouvée
          </p>
        </div>

        <b-table
          v-else
          :items="invoices"
          :fields="fields"
          responsive
          hover
          show-empty
          class="mb-0"
        >
          <template #cell(invoice_number)="data">
            <router-link
              :to="{ name: 'sales-invoice-detail', params: { id: data.item.id } }"
              class="font-weight-bold"
            >
              {{ data.item.invoice_number }}
            </router-link>
          </template>

          <template #cell(period)="data">
            {{ formatDate(data.item.period_start) }} - {{ formatDate(data.item.period_end) }}
          </template>

          <template #cell(total_amount)="data">
            <span class="font-weight-bold text-success">
              {{ formatAmount(data.item.total_amount) }} DT
            </span>
          </template>

          <template #cell(payment_status)="data">
            <b-badge :variant="data.item.payment_status === 'paid' ? 'success' : 'warning'">
              {{ data.item.payment_status_display }}
            </b-badge>
          </template>

          <template #cell(payment_date)="data">
            {{ data.item.payment_date ? formatDate(data.item.payment_date) : '-' }}
          </template>

          <template #cell(actions)="data">
            <b-button
              variant="outline-primary"
              size="sm"
              :to="{ name: 'sales-invoice-detail', params: { id: data.item.id } }"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
              />
              Voir
            </b-button>
          </template>
        </b-table>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BFormSelect,
} from 'bootstrap-vue'

export default {
  name: 'SalesInvoices',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BFormSelect,
  },
  data() {
    return {
      loading: false,
      invoices: [],
      filters: {
        payment_status: '',
      },
      statusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 'unpaid', text: 'Non payée' },
        { value: 'paid', text: 'Payée' },
      ],
      fields: [
        { key: 'invoice_number', label: 'N° Facture', sortable: true },
        { key: 'period', label: 'Période' },
        {
          key: 'total_orders', label: 'Commandes', sortable: true, class: 'text-center',
        },
        {
          key: 'total_amount', label: 'Montant', sortable: true, class: 'text-right',
        },
        { key: 'payment_status', label: 'Statut', class: 'text-center' },
        { key: 'payment_date', label: 'Date Paiement' },
        { key: 'actions', label: 'Actions', class: 'text-center' },
      ],
    }
  },
  mounted() {
    this.loadInvoices()
  },
  methods: {
    async loadInvoices() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.payment_status) {
          params.payment_status = this.filters.payment_status
        }
        const response = await axios.get('/api/commissions/commission-invoices/', { params })
        this.invoices = response.data.results || response.data
      } catch (error) {
        console.error('Error loading invoices:', error)
        this.$bvToast.toast('Erreur lors du chargement des factures', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
  },
}
</script>

<style scoped>
.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.gap-2 {
  gap: 0.5rem;
}
</style>
